import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useLocale } from '@/modules/locale/model/locale';
import { ROUTE_NAME_TRANSLATION } from '@/modules/translation/router';
import { translationService } from '@/services/translation';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useTranslateText() {
    const router = useAppRouter();
    const route = useRoute();
    const crudAction = useCrudActions();

    const { localeSupportedOptions, getListSupported } = useLocale();

    const model = reactive({
        itemId: StringUtils.toNumber(route.params.id),
        locale: (route.query?.locale ?? null) as string,
        hasData: false,
        content: ''
    });

    const validations = {
        content: { required },
        locale: { required }
    };

    const v$ = useVuelidate(validations, model);

    const beforeFetch = async () => {
        await getListSupported();
        if (!localeSupportedOptions.value.length) {
            router.push({ name: ROUTE_NAME_TRANSLATION.INDEX });
        }
    };

    const fetch = crudAction.executeAction({
        canExecute: () => model.locale != null,
        request: () => translationService.getContent(model.itemId, model.locale),
        onSuccess: ({ data }) => {
            model.hasData = true;
            model.content = data;
        },
        onError: () => {
            model.hasData = false;
            model.content = '';
        },
        onFinally: () => v$.value.$reset()
    });

    const actions = {
        updateText: crudAction.excuteAndNotify({
            validator: v$,
            request: () => translationService.updateContent(model.itemId, model.locale, model.content),
            onSuccess: fetch
        }),
        removeText: crudAction.confirmAndRemove({
            request: () => translationService.removeContent(model.itemId, model.locale),
            onSuccess: fetch
        })
    };

    return { v$, model, localeSupportedOptions, fetch, beforeFetch, actions };
}
